const featureGroups = [
  {
    icon: 'ri-box-3-line',
    color: 'text-[#3b82f6]',
    bg: 'bg-[#3b82f6]/10',
    border: 'border-[#3b82f6]/20',
    title: 'Parts Inventory Management',
    description: 'Total control over your parts — from receiving to sale.',
    items: [
      'Real-time stock tracking across all locations',
      'Barcode scanning for fast sales & receiving',
      'Auto reorder levels + PO generation',
      'Supplier price comparison engine',
    ],
    image: 'https://public.readdy.ai/ai/img_res/a52087dc4a7d44fa4b00ab4ce610a929.jpg',
  },
  {
    icon: 'ri-car-line',
    color: 'text-[#f97316]',
    bg: 'bg-[#f97316]/10',
    border: 'border-[#f97316]/20',
    title: 'Vehicle History Tracking',
    description: 'Every vehicle\'s complete service record at your fingertips.',
    items: [
      'Full service history by VIN or registration',
      'Track parts + labour per job card',
      'Warranty tracking with automated alerts',
      'Customer SMS/email service reminders',
    ],
    image: 'https://readdy.ai/api/search-image?query=vehicle%20diagnostic%20screen%20showing%20VIN%20history%20digital%20car%20service%20records%20on%20a%20tablet%20in%20a%20modern%20automotive%20workshop%20orange%20accent%20lighting%20dark%20background%20sleek%20tech%20interface%20professional%20photography&width=600&height=400&seq=feat002&orientation=landscape',
  },
  {
    icon: 'ri-user-heart-line',
    color: 'text-green-400',
    bg: 'bg-green-400/10',
    border: 'border-green-400/20',
    title: 'Customer Portals',
    description: 'Empower your customers with self-service access.',
    items: [
      'Live parts availability checking',
      'Order tracking dashboard',
      'Digital service history access',
      'Online booking system',
    ],
    image: 'https://readdy.ai/api/search-image?query=customer%20self%20service%20automotive%20portal%20on%20laptop%20screen%20showing%20parts%20ordering%20interface%20modern%20SaaS%20dashboard%20dark%20navy%20background%20green%20accent%20colors%20tech%20workspace%20ultra%20realistic%20photography&width=600&height=400&seq=feat003&orientation=landscape',
  },
  {
    icon: 'ri-truck-line',
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10',
    border: 'border-yellow-400/20',
    title: 'Supplier Management',
    description: 'Smarter buying decisions with centralized supplier data.',
    items: [
      'Multi-supplier database management',
      'Purchase order tracking & status',
      'Supplier performance analytics',
      'Automated communications & alerts',
    ],
    image: 'https://readdy.ai/api/search-image?query=supplier%20management%20dashboard%20on%20computer%20screen%20showing%20purchase%20orders%20analytics%20charts%20modern%20tech%20office%20with%20dark%20background%20yellow%20accent%20lights%20automotive%20supply%20chain%20professional%20photography&width=600&height=400&seq=feat004&orientation=landscape',
  },
];

const Features = () => {
  return (
    <section className="bg-[#060f1e] py-24 px-6" id="features">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-5">
            <i className="ri-sparkling-line text-[#f97316] text-sm"></i>
            <span className="text-gray-300 text-xs font-semibold tracking-widest uppercase">Full Feature Suite</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Everything You Need to Run
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#3b82f6] to-[#f97316]">
              a Modern Automotive Business
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Four powerful modules working together seamlessly. Built from the ground up for South African auto businesses.
          </p>
        </div>

        {/* Feature blocks */}
        <div className="space-y-6">
          {featureGroups.map((feat, i) => (
            <div
              key={i}
              className={`grid lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden border ${feat.border} bg-[#0a1628] group hover:border-opacity-50 transition-all duration-300 ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
            >
              {/* Image side */}
              <div className={`relative h-56 lg:h-auto ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                <img
                  src={feat.image}
                  alt={feat.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/60 to-transparent lg:hidden"></div>
              </div>

              {/* Content side */}
              <div className={`p-8 lg:p-10 flex flex-col justify-center ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className={`w-12 h-12 flex items-center justify-center ${feat.bg} rounded-xl mb-5`}>
                  <i className={`${feat.icon} ${feat.color} text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {feat.title}
                </h3>
                <p className="text-gray-400 text-sm mb-6">{feat.description}</p>
                <ul className="space-y-3">
                  {feat.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-3 text-gray-300 text-sm">
                      <div className={`w-5 h-5 flex items-center justify-center ${feat.bg} rounded-full flex-shrink-0`}>
                        <i className={`ri-check-line ${feat.color} text-xs`}></i>
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Extra highlights row */}
        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          {[
            { icon: 'ri-building-line', title: 'Multi-Branch Ready', desc: 'Manage unlimited locations from a single dashboard with real-time sync.' },
            { icon: 'ri-smartphone-line', title: 'Mobile Technician App', desc: 'Techs can update jobs, capture images, and view history on the go.' },
            { icon: 'ri-user-add-line', title: 'Unlimited Users', desc: 'Add your entire team — no per-seat fees. Everyone stays connected.' },
          ].map((h, i) => (
            <div key={i} className="bg-[#0a1628] border border-white/10 rounded-xl p-6 hover:border-[#3b82f6]/30 transition-all duration-300">
              <div className="w-10 h-10 flex items-center justify-center bg-[#3b82f6]/10 rounded-lg mb-4">
                <i className={`${h.icon} text-[#3b82f6] text-xl`}></i>
              </div>
              <h4 className="text-white font-bold text-base mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>{h.title}</h4>
              <p className="text-gray-400 text-sm leading-relaxed">{h.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
